<template>
  <div class="action-block">
    <button class="bttn bttn--sm bttn--orange" :disabled="!hasAccess" @click="openModal">
      {{ $t("gateways.add_gateways", "Add new") }}
    </button>

    <b-modal
      id="gatewayadd"
      size="lg"
      :no-close-on-backdrop="true"
      :hide-footer="!gatewayStep"
      footer-class="gateway-footer"
      @hidden="clearData"
    >
      <div slot="modal-header">
        <h2>
          <span v-if="gatewayStep" @click="backToList">
            <svg
              version="1.1"
              id="Layer_1"
              xmlns="http://www.w3.org/2000/svg"
              xmlns:xlink="http://www.w3.org/1999/xlink"
              x="0px"
              y="0px"
              width="24px"
              height="24px"
              viewBox="0 0 24 24"
              enable-background="new 0 0 24 24"
              xml:space="preserve"
            >
              <path d="M15.41,16.59L10.83,12l4.58-4.59L14,6l-6,6l6,6L15.41,16.59z" />
              <path fill="none" d="M0,0h24v24H0V0z" />
            </svg>
          </span>
          {{ formTitle }}
        </h2>
        <div class="modal-header__hide" @click="modalClose">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
            <path
              d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
            />
            <path d="M0 0h24v24H0z" fill="none" />
          </svg>
        </div>
      </div>
      <div slot="modal-footer">
        <portal-target slim name="gateway-add-footer" />
      </div>

      <div v-if="gatewayDataView">
        <AccGatewayView
          ref="modalViewData"
          :gateway="gatewayDataView"
          @update-step="modalClose"
        ></AccGatewayView>
      </div>

      <div v-else>
        <div v-if="!gatewayStep">
          <!-- No legal entity yet — a gateway must belong to one -->
          <div v-if="hasNoEntities" class="ga-needs-country">
            <h3>{{ $t("gateways.create_entity_title", "Add a legal entity first") }}</h3>
            <p>
              {{
                $t(
                  "gateways.create_entity_text",
                  "A payment gateway belongs to a legal entity. Create one to continue."
                )
              }}
            </p>
            <div class="ga-needs-country__actions">
              <button class="bttn bttn--sm bttn--orange" type="button" @click="createEntity">
                {{ $t("gateways.create_entity_button", "Create legal entity") }}
              </button>
            </div>
          </div>

          <!-- Step 1 · choose the legal entity (skipped when there is only one) -->
          <div v-else-if="showEntityStep" class="ga-entity-step">
            <p class="ga-entity-step__prompt">
              {{
                $t(
                  "gateways.select_entity_prompt",
                  "Select the legal entity this gateway will belong to. Availability depends on its country."
                )
              }}
            </p>
            <button
              v-for="e in entityList"
              :key="e.id"
              type="button"
              class="ga-entity-row"
              @click="pickEntity(e.id)"
            >
              <span class="ga-entity-row__main">
                <span class="ga-entity-row__name">{{ e.name }}</span>
                <span v-if="e.primary" class="ga-tag">{{ $t("gateways.primary", "Primary") }}</span>
                <span v-else-if="!e.country" class="ga-tag ga-tag--warn">
                  {{ $t("gateways.no_country_set", "No country set") }}
                </span>
              </span>
              <span class="ga-entity-row__sub">
                {{ e.countryName || $t("gateways.no_country_set", "No country set") }}
              </span>
            </button>
          </div>

          <!-- Step 2 · the catalog, resolved for the selected entity's country -->
          <div v-else>
            <div v-if="psLoading" class="ga-loading"><LdsSpinner /></div>

            <div v-else-if="psNeedsCountry" class="ga-needs-country">
              <h3>
                {{
                  $t("gateways.entity_needs_country_title", "Set a country for this legal entity")
                }}
              </h3>
              <p>
                {{
                  $t(
                    "gateways.entity_needs_country_text",
                    "Gateway availability is decided by the legal entity's country. Add one to see what you can connect."
                  )
                }}
              </p>
              <div class="ga-needs-country__actions">
                <button class="bttn bttn--sm bttn--orange" type="button" @click="goSetCountry">
                  {{ $t("gateways.set_country", "Set country") }}
                </button>
                <button
                  v-if="entityList.length > 1"
                  class="bttn bttn--sm bttn--bordered"
                  type="button"
                  @click="entityStep = true"
                >
                  {{ $t("common.back", "Back") }}
                </button>
              </div>
            </div>

            <div v-else>
              <div v-if="selectedEntity && entityList.length > 1" class="ga-entity-bar">
                <span class="ga-entity-bar__text">
                  <strong>{{ selectedEntity.name }}</strong>
                  <span v-if="selectedEntity.country" class="ga-cc">{{
                    selectedEntity.country
                  }}</span>
                  <span class="ga-entity-bar__country">{{ selectedEntity.countryName }}</span>
                </span>
                <button type="button" class="ga-link" @click="changeEntity">
                  {{ $t("common.change", "Change") }}
                </button>
              </div>

              <p v-if="psError" class="gateway-form__notice">
                {{
                  $t(
                    "gateways.availability_load_failed",
                    "Couldn't load gateway availability — showing the full list."
                  )
                }}
              </p>

              <div v-if="catalogSections.onboard.length" class="ga-section">
                <h3 class="ga-section__title">
                  {{ $t("gateways.section_onboard", "Set up a new account with Charidy") }}
                </h3>
                <div class="ga-rows">
                  <GatewayAddRow
                    v-for="it in catalogSections.onboard"
                    :key="'onb-' + it.key"
                    :name="it.name"
                    :system-name="it.systemName"
                    :description="it.description"
                    :logo="it.logo"
                    :methods="it.methods"
                    :disabled="it.needsApproval"
                    :note="
                      it.needsApproval ? $t('gateways.approval_needed', 'Approval needed') : ''
                    "
                    :action-label="$t('gateways.set_up', 'Set up')"
                    @click="onCatalogPick(it)"
                  />
                </div>
              </div>

              <div class="ga-section">
                <h3 class="ga-section__title">
                  {{ $t("gateways.section_connect", "Connect an existing gateway") }}
                </h3>

                <div class="ga-search">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                  >
                    <circle cx="11" cy="11" r="7" />
                    <path d="M21 21l-4.3-4.3" />
                  </svg>
                  <input
                    type="text"
                    :placeholder="$t('gateways.search_gateways', 'Search gateways…')"
                    v-model="search"
                  />
                </div>

                <p v-if="hasNeedsApproval" class="ga-approval-note">
                  {{
                    $t(
                      "gateways.needs_approval_note",
                      "Some gateways need approval first. Please contact your campaign manager to connect to these gateways."
                    )
                  }}
                </p>

                <div v-if="catalogSections.connect.length" class="ga-rows">
                  <GatewayAddRow
                    v-for="it in catalogSections.connect"
                    :key="'con-' + it.key"
                    :name="it.name"
                    :system-name="it.systemName"
                    :description="it.description"
                    :logo="it.logo"
                    :methods="it.methods"
                    :disabled="it.needsApproval"
                    :note="
                      it.needsApproval ? $t('gateways.approval_needed', 'Approval needed') : ''
                    "
                    :action-label="$t('gateways.connect_gateway', 'Connect')"
                    @click="onCatalogPick(it)"
                  />
                </div>
                <p v-else class="ga-empty">
                  {{ $t("gateways.no_gateways_match", "No gateways match your search.") }}
                </p>
              </div>

              <div v-if="catalogSections.unavailable.length" class="ga-unavailable">
                <button
                  type="button"
                  class="ga-disclosure"
                  @click="showUnavailable = !showUnavailable"
                >
                  <span
                    class="ga-disclosure__caret"
                    :class="{ 'ga-disclosure__caret--open': showUnavailable }"
                    >&#9656;</span
                  >
                  {{ unavailableLabel }}
                  <span class="ga-disclosure__count">{{ catalogSections.unavailable.length }}</span>
                </button>
                <div v-if="showUnavailable" class="ga-rows ga-dim">
                  <GatewayAddRow
                    v-for="it in catalogSections.unavailable"
                    :key="'un-' + it.key"
                    :name="it.name"
                    :system-name="it.systemName"
                    :description="it.description"
                    :logo="it.logo"
                    :methods="it.methods"
                    :disabled="true"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else>
          <component
            v-if="currentComponent"
            :is="currentComponent.component"
            :entities="formEntities"
            :entitiesFullList="formEntitiesFullList"
            :preselectedEntityId="selectedEntityId"
            :editdata="editGatewaData"
            :stripeAccounts="stripeAccounts"
            :canCreateStripeAccount="canCreateStripeAccount"
            :gatewayName="currentComponent.gatewayName"
            :isBit="currentComponent.isBit"
            :isAch="currentComponent.isAch"
            :isWallet="currentComponent.isWallet"
            :direct="currentComponent.direct"
            :isTerminal="currentComponent.isTerminal"
            :isApplePay="currentComponent.isApplePay"
            :isDaf="currentComponent.isDaf"
            @close-modal="modalClose()"
            @back-to-list="backToList"
            @create-entity="createEntity"
            @create-stripe-acc="gatewayStep = 'stripe'"
          />
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapState } from "vuex";
import GatewayAddRow from "./gatewayAddRow.vue";
import AccGatewayView from "./accGatewayView.vue";
import LdsSpinner from "@/common-components/ui-elements/lds-spinner.vue";
import { getAltNames } from "@/constants/gateway-alt-names";
import { countryList } from "@/constants/countries";
import { usePaymentSystemListApi } from "@/composables/payment-system-list";

// Payment-system availability states returned by
// GET /organization/:oid/payment_system_list?legal_entity_id=… — see the
// Gateway Availability spec.
const AVAILABILITY_AVAILABLE = "available";
const AVAILABILITY_NEEDS_MANAGER = "needs_manager";
const AVAILABILITY_UNAVAILABLE_COUNTRY = "unavailable_country";

// Payment-method token -> i18n key + English fallback, for the row pills.
const METHOD_LABELS = {
  card: ["gateways.method_card", "Cards"],
  apple_pay: ["gateways.method_apple_pay", "Apple Pay"],
  google_pay: ["gateways.method_google_pay", "Google Pay"],
  wallet: ["gateways.method_wallet", "Wallet"],
  bank_transfer: ["gateways.method_bank_transfer", "Bank transfer"],
  ach: ["gateways.method_ach", "ACH"],
  direct_debit: ["gateways.method_direct_debit", "Direct debit"],
  crypto: ["gateways.method_crypto", "Crypto"],
  daf: ["gateways.method_daf", "DAF"],
  voucher: ["gateways.method_voucher", "Voucher"],
};

function humanizeGatewayKey(key) {
  return key
    .replace(/[-_]+/g, " ")
    .replace(/\bv(\d)\b/gi, "V$1")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default {
  GATEWAY_LIST_CONFIG: {
    stripe: {
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/charidy_images/gateways/stripe.png",
      component: () => import("./accStripe.vue"),
    },
    "stripe-element": {
      title: "Element",
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/charidy_images/gateways/stripe.png",
      gatewayName: "stripe-element",
      component: () => import("./accFromStripe.vue"),
    },
    "stripe-ideal": {
      title: "Stripe iDeal",
      gatewayName: "stripe-ideal",
      component: () => import("./accFromStripe.vue"),
    },
    "stripe-tap-to-pay": {
      title: "Stripe Tap to Pay",
      gatewayName: "stripe-tap-to-pay",
      component: () => import("./accFromStripe.vue"),
    },
    // systemNames: the payment_system_list rows behind a key whose name differs
    // from the backend system_name.
    stripe_becs: {
      title: "Stripe BECS",
      systemNames: ["stripe-becs"],
      component: () => import("./accStripeBecs.vue"),
    },
    stripe_apple_google_pay: {
      title: "Stripe Apple/Google Pay",
      systemNames: ["stripe-apple-pay", "stripe-google-pay"],
      component: () => import("./accStripeAppleGooglePay.vue"),
    },
    yaadpay: {
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/charidy_images/gateways/yaadpay.png",
      component: () => import("./accYaadpay.vue"),
    },
    paypal: {
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/charidy_images/gateways/paypal.png",
      component: () => import("./accPaypal.vue"),
    },
    paypalv2: {
      img: "https://images.ctfassets.net/gkyt4bl1j2fs/273APNSE63eFeQGVaNokaJ/da5948b50660aa0a99b02ea2e6990e2f/Blue.png?w=424&h=84&q=50&fm=webp&bg=transparent",
      title: "PayPal V2",
      component: () => import("./accPaypalV2.vue"),
    },
    israeltoremet: {
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/charidy_images/gateways/israeltoremet.png",
      component: () => import("./accIsraeltoremet.vue"),
    },
    payfast: {
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/charidy_images/gateways/payfast.png",
      component: () => import("./accFastPay.vue"),
    },
    payme: {
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/charidy_images/gateways/payme_01.png",
      component: () => import("./accPayme.vue"),
    },
    "payme-card": {
      title: "PayMe Card",
      gatewayName: "payme-card",
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/charidy_images/gateways/payme_01.png",
      component: () => import("./accPaymeCard.vue"),
    },
    "payme-bit": {
      title: "PayMe Bit",
      gatewayName: "payme-bit",
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/charidy_images/1603128111820534712.png",
      component: () => import("./accPaymeCard.vue"),
    },
    broom: {
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/charidy_images/gateways/broom.jpg",
      component: () => import("./accBroomFoundation.vue"),
    },
    asser: {
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/charidy_images/gateways/asseb.png",
      component: () => import("./accAsser.vue"),
    },
    achisomoch: {
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/public_upload/1708023150_6431333764373962656534623831383666616564336630383931616431636637_31373038303233313530.png",
      component: () => import("./accAchisomoch.vue"),
    },
    braintree: {
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/charidy_images/1560882051285651734.png",
      component: () => import("./accBraintree.vue"),
    },
    mercadopago: {
      img: "https://http2.mlstatic.com/ui/navigation/4.1.4/mercadopago/logo__large@2x.png",
      component: () => import("./accMercadoPago.vue"),
    },
    "hk-bank-transfer": {
      title: "HK Bank Transfer",
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/charidy_images/business-and-finance-glyph-10-512.png",
      component: () => import("./accHkBankTransfer.vue"),
    },
    pledge: {
      title: "Pledge",
      component: () => import("./accPledge.vue"),
    },
    ojc: {
      title: "OJC",
      component: () => import("./accOjc.vue"),
    },
    paygate: {
      title: "PayGate",
      component: () => import("./accPayGate.vue"),
    },
    paytrail: {
      title: "Paytrail",
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/public_upload/1633978140_8f30fd61588aea0ab2a016859da18522_1633978140.jpg",
      component: () => import("./accCheckoutFI.vue"),
    },
    meshulam: {
      title: "Meshulam",
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/charidy_images/1603128033630529584.png",
      component: () => import("./accMeshulam.vue"),
    },
    "meshulam-bit": {
      title: "Meshulam-Bit",
      isBit: true,
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/charidy_images/1603128111820534712.png",
      component: () => import("./accMeshulamBit.vue"),
    },
    "meshulam-google-pay": {
      title: "Meshulam Google Pay",
      gatewayName: "meshulam-google-pay",
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/images/281/Google_Pay_Logo.svg_1699261306.png",
      component: () => import("./accMeshulam.vue"),
    },
    "meshulam-bank-transfer": {
      title: "Meshulam Bank Transfer",
      gatewayName: "meshulam-bank-transfer",
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/charidy_images/business-and-finance-glyph-10-512.png",
      component: () => import("./accMeshulam.vue"),
    },
    "meshulam-wallet": {
      title: "Meshulam Wallet",
      gatewayName: "meshulam-wallet",
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/charidy_images/1766310486923998628.png",
      component: () => import("./accMeshulam.vue"),
    },
    mhtrust: {
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/public_upload/1654181780_8aa03b8a611b307207537b5b703dadea_1654181780.png",
      component: () => import("./accMhTrust.vue"),
    },
    zoreyatzedokos: {
      title: "Zoreya Tzedokos",
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/public_upload/1625604140_335bf636f6a7b54521e0a8fb73a41d80_1625604140.png",
      component: () => import("./accZoreyaTzedokos.vue"),
    },
    threepillars: {
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/public_upload/1625604113_5adf8a08f84b634255601c00731b1ff6_1625604113.png",
      component: () => import("./accThreepillars.vue"),
    },
    kolyom: {
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/charidy_images/1618239698909704069.jpg",
      component: () => import("./accKolYom.vue"),
    },
    kcbobov: {
      img: "https://online.kcbobov.co.uk/assets/images/logo.png",
      component: () => import("./accKcbobov.vue"),
    },
    yadshlomo: {
      img: "https://yadshlomo.org/assets/images/logo.png",
      component: () => import("./accYadShlomo.vue"),
    },
    belzvouchers: {
      img: "https://belzvouchers.com/assets/images/logo.png",
      component: () => import("./accBelzVouchers.vue"),
    },
    uta: {
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/public_upload/1708023171_3761373764663633633838326462383338363065363231333932356236323265_31373038303233313731.png",
      component: () => import("./accUTA.vue"),
    },
    cmz: {
      img: "https://online.cmz.org.uk/assets/images/logo.png",
      component: () => import("./accCMZ.vue"),
    },
    smartgiving: {
      img: "https://smartgiving.org.uk/wp-content/uploads/2022/05/Asset-4SmartGiving_Logo_RGB.png",
      component: () => import("./accSmartGiving.vue"),
    },
    tevini: {
      img: "https://www.tevini.co.uk/assets/image/logo/logo.png",
      component: () => import("./accTevini.vue"),
    },
    dlocal: {
      title: "DLocal",
      component: () => import("./accDLocal.vue"),
    },
    nedarimplus: {
      title: "NedarimPlus",
      component: () => import("./accNedarimPlus.vue"),
    },
    nedarimplusbit: {
      title: "NedarimPlusBit",
      gatewayName: "nedarimplus-bit",
      component: () => import("./accNedarimPlus.vue"),
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/charidy_images/1603128111820534712.png",
    },
    nedarimplusbanktransfer: {
      title: "NedarimPlus Bank Transfer",
      gatewayName: "nedarimplus-bank-transfer",
      component: () => import("./accNedarimPlus.vue"),
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/charidy_images/business-and-finance-glyph-10-512.png",
    },
    tranzila: {
      title: "Tranzila",
      component: () => import("./accTranzila.vue"),
    },
    "tranzila-bit": {
      title: "Tranzila-Bit",
      isBit: true,
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/charidy_images/1603128111820534712.png",
      component: () => import("./accTranzilaBit.vue"),
    },
    funds: {
      title: "Funds",
      component: () => import("./accFunds.vue"),
    },
    pelecard: {
      title: "Pelecard",
      component: () => import("./accPelecard.vue"),
    },
    walletdoc: {
      title: "Walletdoc",
      component: () => import("./accWalletdoc.vue"),
    },
    coinbase: {
      title: "Coinbase",
      component: () => import("./accCoinbase.vue"),
    },
    cardcom: {
      title: "CardCom",
      component: () => import("./accCardCom.vue"),
    },
    "cardcom-bit": {
      title: "CardCom Bit",
      component: () => import("./accCardCom.vue"),
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/charidy_images/1603128111820534712.png",
      isBit: true,
    },
    "icredit-rivhit": {
      title: "iCredit",
      component: () => import("./accICreditRivhit.vue"),
    },
    jaffa: {
      title: "Jaffa",
      component: () => import("./accJaffa.vue"),
    },
    "jaffa-bit": {
      title: "Jaffa Bit",
      isBit: true,
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/charidy_images/1603128111820534712.png",
      component: () => import("./accJaffaBit.vue"),
    },
    "blink-fidelipay": {
      title: "Blink Fidelipay",
      component: () => import("./accBlinkFidelipay.vue"),
    },
    aminut: {
      title: "Aminut",
      component: () => import("./accAminut.vue"),
    },
    cardknox: {
      title: "Cardknox",
      component: () => import("./accCardknox.vue"),
    },
    kehilot: {
      title: "Kehilot",
      component: () => import("./accKehilot.vue"),
    },
    "kehilot-bit": {
      title: "Kehilot Bit",
      isBit: true,
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/charidy_images/1603128111820534712.png",
      component: () => import("./accKehilot.vue"),
    },
    donorsfund: {
      title: "The Donors Fund",
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/images/281/Stacked_1667904124.png",
      component: () => import("./accDonorsFund.vue"),
    },
    pledger: {
      title: "Pledger",
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/public_upload/1736376580_3936643666326537653166373035616235653539633834613664633030396232_31373336333736353830.svg",
      component: () => import("./accPledger.vue"),
    },
    "pledger-direct": {
      title: "Pledger Direct",
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/public_upload/1736376580_3936643666326537653166373035616235653539633834613664633030396232_31373336333736353830.svg",
      direct: true,
      component: () => import("./accPledger.vue"),
    },
    mancal: {
      title: "Mancal",
      component: () => import("./accMancal.vue"),
    },
    "mancal-bit": {
      title: "Mancal Bit",
      isBit: true,
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/charidy_images/1603128111820534712.png",
      component: () => import("./accMancal.vue"),
    },
    usaepay: {
      title: "USAePay",
      component: () => import("./accUsaepay.vue"),
    },
    banquest: {
      title: "Banquest",
      component: () => import("./accBanquest.vue"),
    },
    "banquest-ach": {
      title: "Banquest ACH",
      isAch: true,
      component: () => import("./accBanquest.vue"),
    },
    "banquest-terminal": {
      title: "Banquest Terminal",
      isTerminal: true,
      component: () => import("./accBanquest.vue"),
    },
    "banquest-apple-pay": {
      title: "Banquest Apple Pay",
      isApplePay: true,
      component: () => import("./accBanquest.vue"),
    },
    "banquest-daf": {
      title: "Banquest DAF",
      isDaf: true,
      component: () => import("./accBanquest.vue"),
    },
    tapdash: {
      title: "TapDash",
      component: () => import("./accTapdash.vue"),
    },
    gama: {
      title: "Gama",
      component: () => import("./accGama.vue"),
    },
    "gama-bit": {
      title: "Gama Bit",
      isBit: true,
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/charidy_images/1603128111820534712.png",
      component: () => import("./accGama.vue"),
    },
    donary: {
      title: "Donary",
      component: () => import("./accDonary.vue"),
    },
    authorize: {
      title: "Authorize",
      component: () => import("./accAuthorize.vue"),
    },
    payarc: {
      title: "Payarc",
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/public_upload/1736378147_6633336330623263336566346165316661363330383435353666373031316430_31373336333738313437.svg",
      component: () => import("./accPayarc.vue"),
    },
    "payarc-wallet": {
      title: "Payarc (Apple/Google Pay)",
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/public_upload/1736378147_6633336330623263336566346165316661363330383435353666373031316430_31373336333738313437.svg",
      component: () => import("./accPayarc.vue"),
      isWallet: true,
    },
    matbia: {
      title: "Matbia",
      component: () => import("./accMatbia.vue"),
    },
    peach: {
      title: "Peach",
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/images/281/logo-PEACH_1675767208.png",
      component: () => import("./accPeach.vue"),
    },
    "peach-bit": {
      title: "Peach Bit",
      isBit: true,
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/charidy_images/1603128111820534712.png",
      component: () => import("./accPeach.vue"),
    },
    "powerdoc-direct-debit": {
      title: "Powerdoc Direct Debit",
      isBit: true,
      component: () => import("./accPowerdocDirectDebit.vue"),
    },
    sumit: {
      title: "Sumit",
      component: () => import("./accSumit.vue"),
    },
    chariot: {
      title: "Chariot",
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/public_upload/1718029313_3237646230636631376635313234346337326330633030643162356332333230_31373138303239333133.svg",
      component: () => import("./accChariot.vue"),
    },
    asaas: {
      title: "ASAAS",
      img: "https://files.readme.io/402079d-shape.svg",
      component: () => import("./accASAAS.vue"),
    },
    razorpay: {
      title: "Razorpay",
      component: () => import("./accRazorpay.vue"),
    },
    square: {
      title: "Square",
      component: () => import("./accSquare.vue"),
    },
    imaaser: {
      title: "Imaaser",
      component: () => import("./accImaaser.vue"),
    },
    "imaaser-card": {
      title: "Imaaser Card",
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/charidy_images/1772011919249148549.svg",
      component: () => import("./accImaaserCard.vue"),
    },
    crowded: {
      title: "Crowded",
      img: "https://content.pstmn.io/aa044a46-6390-49c1-a025-0b1a53b78cb7/Y3Jvd2RlZC1sb2dvLnBuZw==",
      component: () => import("./accCrowded.vue"),
    },
    takzivit: {
      title: "Takzivit",
      component: () => import("./accTakzivit.vue"),
    },
    "takzivit-bit": {
      title: "Takzivit Bit",
      gatewayName: "takzivit-bit",
      img: "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/charidy_images/1603128111820534712.png",
      component: () => import("./accTakzivit.vue"),
    },
  },

  components: {
    GatewayAddRow,
    AccGatewayView,
    LdsSpinner,
  },

  data() {
    return {
      gatewayStep: "",
      gatewayDataView: false,
      formTitle: "Add Payment Gateway",
      editGatewaData: null,

      // Entity-first Add Gateway flow
      entityStep: false,
      selectedEntityId: null,
      psResolved: [],
      psLoading: false,
      psError: false,
      psNeedsCountry: false,
      showUnavailable: false,
      search: "",
      // Snapshot of entity ids taken when the "create legal entity" prompt is
      // used, so the flow can auto-select the one that was just created when it
      // reopens. One-shot: consumed in startEntityFlow.
      entityIdsBeforeCreate: null,
    };
  },

  computed: {
    ...mapState({
      hasAccess: (state) => {
        return state.account.access.CanEditOrganizationGatewayList;
      },

      stripeAccounts: (state) => {
        return state.gateways.gateways.filter(
          (g) => g.attributes.gateway === "stripe" && g.attributes.active
        );
      },

      entities: function (state) {
        let entitiesList = state.account.entities;

        let resultArr = [];

        if (entitiesList.length > 1) {
          let defaultObj = {
            text: this.$t("gateways.select_entities"),
            value: 0,
            disabled: true,
          };

          resultArr.push(defaultObj);
        }

        for (var i = 0; i < entitiesList.length; i++) {
          let obj = {
            text: entitiesList[i].attributes.name,
            value: parseInt(entitiesList[i].id),
          };

          resultArr.push(obj);
        }

        return resultArr;
      },

      entitiesFullList(state) {
        let entitiesList = state.account.entities;

        let resultArr = [];

        if (entitiesList.length > 1) {
          let defaultObj = {
            text: this.$t("gateways.select_entities"),
            value: null,
            disabled: true,
          };

          resultArr.push(defaultObj);
        }

        for (var i = 0; i < entitiesList.length; i++) {
          let obj = {
            text: entitiesList[i].attributes.name,
            value: entitiesList[i],
          };

          resultArr.push(obj);
        }

        return resultArr;
      },

      useStripeOnboarding: (state) => state.account.useStripeOnboarding,
      stripeConnectOrgAccount: (state) => state.account.stripeConnectOrgAccount,
      accountEntities: (state) => state.account.entities,
    }),

    // The gateway forms drop their own entity picker: once an entity is chosen
    // in the flow, the forms receive just that one, and their existing
    // "entities.length === 1 -> auto-select" path locks it in.
    formEntities() {
      if (!this.selectedEntity) {
        return this.entities;
      }
      return [{ text: this.selectedEntity.name, value: this.selectedEntity.id }];
    },

    formEntitiesFullList() {
      if (!this.selectedEntityId) {
        return this.entitiesFullList;
      }
      const raw = (this.accountEntities || []).find(
        (e) => parseInt(e.id) === this.selectedEntityId
      );
      return raw ? [{ text: raw.attributes.name, value: raw }] : this.entitiesFullList;
    },

    gatewayListFiltered() {
      const list = { ...this.$options.GATEWAY_LIST_CONFIG };

      // Stripe form routing (see the Gateway Availability spec decision table):
      // the connect setting keeps today's dual connect/create form; otherwise
      // the onboarding setting swaps in the custom-connected-account form.
      if (list.stripe && this.useStripeOnboarding && !this.stripeConnectOrgAccount) {
        list.stripe = {
          ...list.stripe,
          component: () => import("./accStripeOnboarding.vue"),
        };
      }

      return list;
    },

    currentComponent() {
      const gateway = this.gatewayListFiltered[this.gatewayStep];
      if (gateway) {
        return gateway;
      }

      return null;
    },

    entityList() {
      return (this.accountEntities || []).map((e) => {
        const country = (e.attributes.country || "").toUpperCase();
        const match = countryList.find((c) => c.iso_code === country);
        return {
          id: parseInt(e.id),
          name: e.attributes.name,
          primary: !!e.attributes.primary,
          country,
          countryName: match ? match.name : country,
        };
      });
    },

    selectedEntity() {
      return this.entityList.find((e) => e.id === this.selectedEntityId) || null;
    },

    showEntityStep() {
      return this.entityStep && !this.gatewayStep && !this.gatewayDataView;
    },

    hasNoEntities() {
      return this.entityList.length === 0;
    },

    unavailableLabel() {
      const country = (this.selectedEntity && this.selectedEntity.countryName) || "";
      return `${this.$t("gateways.not_available_here", "Not available for")} ${country}`.trim();
    },

    resolvedMap() {
      const map = {};
      (this.psResolved || []).forEach((row) => {
        if (row && row.system_name) {
          map[row.system_name] = row;
        }
      });
      return map;
    },

    // Whether the selected entity may add Stripe itself — false where the
    // backend hides it (e.g. the US hide rule), so the "create stripe account"
    // shortcut in the stripe-derived forms can't route around the catalog.
    canCreateStripeAccount() {
      const resolution = this.resolvedMap.stripe || this.inferResolution("stripe");
      return resolution.availability === AVAILABILITY_AVAILABLE;
    },

    catalogSections() {
      const sections = { onboard: [], connect: [], unavailable: [] };
      const q = this.search.trim().toLowerCase();

      Object.entries(this.gatewayListFiltered).forEach(([key, cfg]) => {
        const api = this.resolvedRowFor(key, cfg);
        const resolution = api || this.inferResolution(key);
        const availability = resolution.availability;
        const flows = resolution.flows || [];

        const name = (api && api.label) || cfg.title || humanizeGatewayKey(key);
        const systemName = humanizeGatewayKey((api && api.system_name) || key);
        const description = (api && api.description) || "";
        const methods = ((api && api.methods) || []).map((m) => this.methodLabel(m));

        const item = {
          key,
          name,
          systemName,
          description,
          logo: cfg.img || (api && api.logo) || "",
          methods,
          availability,
          flows,
          needsApproval: availability === AVAILABILITY_NEEDS_MANAGER,
        };

        if (availability === AVAILABILITY_UNAVAILABLE_COUNTRY) {
          sections.unavailable.push(item);
          return;
        }

        // Search filters only the connect list (onboarding is 1–2 fixed items).
        const matches =
          !q ||
          name.toLowerCase().includes(q) ||
          key.toLowerCase().includes(q) ||
          description.toLowerCase().includes(q);

        if (flows.includes("onboard")) {
          sections.onboard.push(item);
        }
        if (flows.includes("connect") && matches) {
          sections.connect.push(item);
        }
      });

      return sections;
    },

    hasNeedsApproval() {
      const s = this.catalogSections;
      return [...s.onboard, ...s.connect].some((it) => it.needsApproval);
    },
  },

  methods: {
    // Method token -> display label. Unknown tokens (added later in
    // payment_systems.meta) fall back to a humanised form so they still render.
    methodLabel(token) {
      const known = METHOD_LABELS[token];
      if (known) {
        return this.$t(known[0], known[1]);
      }
      return humanizeGatewayKey(String(token));
    },

    backToList: function () {
      this.gatewayStep = "";
      this.formTitle = this.$t("gateways.add_gateways");
    },

    openModal(gatewayStep) {
      this.resetFlow();
      this.formTitle = this.$t("gateways.add_gateways");
      this.$root.$emit("bv::show::modal", "gatewayadd");

      // Direct jump straight to a gateway form — e.g. resuming after creating a
      // legal entity mid-flow. The form keeps its own entity picker.
      if (typeof gatewayStep === "string" && gatewayStep) {
        this.gatewaysHandle(gatewayStep);
        return;
      }

      this.startEntityFlow();
    },

    startEntityFlow() {
      const entities = this.entityList;

      // Just came back from creating a legal entity — go straight to that one.
      const knownIds = this.entityIdsBeforeCreate;
      this.entityIdsBeforeCreate = null;
      if (knownIds) {
        const created = entities.find((e) => !knownIds.includes(e.id));
        if (created) {
          this.selectedEntityId = created.id;
          this.entityStep = false;
          this.fetchPaymentSystems();
          return;
        }
      }

      if (entities.length === 0) {
        // The template shows the "create legal entity" prompt (hasNoEntities).
        this.selectedEntityId = null;
        this.entityStep = false;
        return;
      }

      if (entities.length === 1) {
        this.selectedEntityId = entities[0].id;
        this.entityStep = false;
        this.fetchPaymentSystems();
        return;
      }

      const primary = entities.find((e) => e.primary);
      this.selectedEntityId = primary ? primary.id : entities[0].id;
      this.entityStep = true;
    },

    pickEntity(id) {
      this.selectedEntityId = id;
      this.entityStep = false;
      this.showUnavailable = false;
      this.fetchPaymentSystems();
    },

    changeEntity() {
      this.entityStep = true;
      this.showUnavailable = false;
    },

    fetchPaymentSystems() {
      const orgId = this.$store.state.account.orgId || localStorage.getItem("orgId");
      if (!orgId || !this.selectedEntityId) {
        this.psResolved = [];
        return;
      }

      this.psLoading = true;
      this.psError = false;
      this.psNeedsCountry = false;

      const { getPaymentSystemList } = usePaymentSystemListApi(orgId);
      getPaymentSystemList({ legal_entity_id: this.selectedEntityId })
        .then((res) => {
          this.psNeedsCountry = !!res.needs_country;
          this.psResolved = res.data || [];
        })
        .catch(() => {
          this.psError = true;
          this.psResolved = [];
        })
        .finally(() => {
          this.psLoading = false;
        });
    },

    onCatalogPick(item) {
      if (item.availability === AVAILABILITY_UNAVAILABLE_COUNTRY || item.needsApproval) {
        return;
      }
      this.gatewaysHandle(item.key);
    },

    goSetCountry() {
      this.modalClose();
      this.$router.push("/account").catch(() => {});
    },

    // The backend row for a catalog key. A key covering several systems is
    // available if any of them is, so it is hidden only when all of them are.
    resolvedRowFor(key, cfg) {
      const rows = (cfg.systemNames || [key]).map((n) => this.resolvedMap[n]).filter(Boolean);
      if (rows.length === 0) {
        return null;
      }
      return rows.find((r) => r.availability !== AVAILABILITY_UNAVAILABLE_COUNTRY) || rows[0];
    },

    // Fallback when the backend returns no row for a gateway (or the call
    // failed): never hide it, just place it in the sensible section.
    inferResolution(key) {
      if (key === "crowded" || key.indexOf("crowded-") === 0) {
        return { availability: AVAILABILITY_AVAILABLE, flows: ["onboard"] };
      }
      if (key === "stripe") {
        return { availability: AVAILABILITY_AVAILABLE, flows: ["onboard"] };
      }
      return { availability: AVAILABILITY_AVAILABLE, flows: ["connect"] };
    },

    resetFlow() {
      this.gatewayStep = "";
      this.entityStep = false;
      this.selectedEntityId = null;
      this.psResolved = [];
      this.psLoading = false;
      this.psError = false;
      this.psNeedsCountry = false;
      this.showUnavailable = false;
    },

    modalClose() {
      this.$root.$emit("bv::hide::modal", "gatewayadd");
    },

    createEntity: function () {
      this.entityIdsBeforeCreate = this.entityList.map((e) => e.id);
      this.modalClose();
      this.$emit("create-entity", this.gatewayStep);
    },

    clearData: function () {
      this.formTitle = "";

      if (this.gatewayDataView) {
        this.gatewayDataView = false;
      }

      this.editGatewaData = null;
      this.resetFlow();
    },

    openModalView(getawaysIngo) {
      this.formTitle =
        getAltNames(getawaysIngo.attributes.gateway) ||
        this.$t(`gateways.view_data_${getawaysIngo.attributes.gateway}`);
      this.gatewayDataView = getawaysIngo;
      this.$root.$emit("bv::show::modal", "gatewayadd");
    },

    openEdit(obj) {
      this.$refs.modalViewData.clearDataModa();
      this.gatewayDataView = false;
      this.gatewayStep = obj.getwayname;
      this.editGatewaData = obj.data;
    },

    gatewaysHandle(props) {
      this.gatewayStep = props;
      this.formTitle = this.$t("gateways.add_" + props);
    },
  },
};
</script>

<style lang="scss" scoped>
.modal-header {
  position: relative;
  h2 {
    padding: 0;
    display: flex;
    align-items: center;
    position: relative;
    span {
      display: inline-block;
      cursor: pointer;
      margin-right: 10px;
      position: absolute;
      top: 0;
      left: -30px;
      svg {
        fill: gray;
      }
    }
  }
}
#gatewayadd {
  .gateway-form {
    .form-block {
      margin-bottom: 20px;

      h3 {
        font-size: 24px;
        color: #20434e;
        padding: 10px 0;
      }

      label,
      .b-skeleton {
        margin-bottom: 10px;
        padding: 0;
        font-family: $vito-light;
        text-transform: uppercase;
        font-size: 14px;
        color: $fd;
      }
    }

    &__action {
      display: flex;
      width: 100%;
      justify-content: space-between;
    }

    &__notice {
      margin-bottom: 20px;
      font-size: 16px;
      color: #20434e;

      a {
        font-size: 16px;
        font-weight: 500;
        color: #cc6d4c;

        &:hover,
        &:focus {
          text-decoration: none;
        }
      }
    }
  }
  .gateway-footer {
    display: flex;
    width: 100%;
    ::v-deep .vue-portal-target {
      display: flex;
      width: 100%;
    }
  }
}

:deep(.gateway-form) {
  .gateway-form {
    &__action {
      margin-bottom: 20px;
      display: flex;
      justify-content: space-between;
    }
  }
}

/* ---- entity-first Add Gateway flow ---- */
.ga-entity-step {
  &__prompt {
    font-size: 14px;
    color: $fd;
    margin-bottom: 16px;
  }
}

.ga-entity-row {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  gap: 4px;
  padding: 14px 18px;
  margin-bottom: 10px;
  background: #fff;
  border: 1px solid rgba(32, 67, 78, 0.15);
  border-radius: 4px;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;

  &:hover {
    border-color: $bb-100;
    background: $bg-50;
  }

  &__main {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__name {
    font-family: $vito-bold;
    font-size: 16px;
    color: #20434e;
  }

  &__sub {
    font-size: 13px;
    color: #7c8798;
  }
}

.ga-tag {
  font-family: $vito-bold;
  font-size: 11px;
  letter-spacing: 0.02em;
  padding: 2px 8px;
  border-radius: 999px;
  color: #3a7d6a;
  background: #e6f6f0;

  &--warn {
    color: #b06a12;
    background: #fdf3e2;
  }
}

.ga-entity-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 11px 16px;
  margin-bottom: 16px;
  background: $bg-50;
  border: 1px solid $bg-100;
  border-radius: 4px;

  &__text {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;

    strong {
      font-family: $vito-bold;
      color: #20434e;
    }
  }

  &__country {
    font-size: 13px;
    color: #7c8798;
  }
}

.ga-cc {
  font-family: $vito-bold;
  font-size: 11px;
  letter-spacing: 0.04em;
  color: $bb-100;
  background: #e4f3f7;
  padding: 2px 7px;
  border-radius: 3px;
}

.ga-link {
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
  font-family: $vito-bold;
  font-size: 13px;
  color: $bb-100;

  &:hover {
    color: $bb-90;
  }
}

.ga-section {
  margin-bottom: 22px;

  &__title {
    font-family: $vito-bold;
    font-size: 12px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #6b7686;
    padding-bottom: 8px;
    margin-bottom: 12px;
    border-bottom: 1px solid #eef1f5;
  }
}

.ga-rows {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ga-search {
  position: relative;
  margin-bottom: 14px;

  svg {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #9aa4b2;
  }

  input {
    width: 100%;
    height: 38px;
    border: 1px solid rgba(32, 67, 78, 0.15);
    border-radius: 4px;
    padding: 0 12px 0 36px;
    font-family: $vito-light;
    font-size: 14px;
    color: $fd;
    background: #fff;

    &:focus {
      outline: 0;
      border-color: $bb-100;
    }
  }
}

.ga-empty {
  padding: 24px 12px;
  text-align: center;
  font-size: 13px;
  color: #9aa4b2;
}

.ga-approval-note {
  font-size: 13px;
  color: #8a5b18;
  background: #fdf3e2;
  border: 1px solid #f6e3c4;
  border-radius: 4px;
  padding: 10px 13px;
  margin-bottom: 12px;
}

.ga-loading {
  display: flex;
  justify-content: center;
  padding: 40px 0;
}

.ga-needs-country {
  text-align: center;
  padding: 24px 20px 12px;

  h3 {
    font-family: $vito-bold;
    font-size: 17px;
    color: #20434e;
    margin-bottom: 8px;
  }

  p {
    font-size: 14px;
    color: #7c8798;
    max-width: 44ch;
    margin: 0 auto 18px;
  }

  &__actions {
    display: flex;
    gap: 10px;
    justify-content: center;
  }
}

.ga-unavailable {
  margin-top: 8px;
}

.ga-disclosure {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: 0;
  border-top: 1px solid #eef1f5;
  padding: 14px 2px 4px;
  cursor: pointer;
  font-family: $vito-bold;
  font-size: 13px;
  color: #6b7686;

  &__caret {
    display: inline-block;
    transition: transform 0.15s ease;

    &--open {
      transform: rotate(90deg);
    }
  }

  &__count {
    color: #9aa4b2;
  }
}

.ga-dim {
  opacity: 0.85;
}
</style>
